import { Avatar, Box, Skeleton, Typography } from "@mui/material";
import { colors } from "../../../theme/palette";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { isUserOnline } from "../../../utils/formatDate";
import { Chat, UserInfo } from "../../../types";
import { getEntityDetails } from "../../../api/entity";
import { ActiveIcon } from "../../../assets/icons/MUI/ChatIcons";
import { useFormattedTimestamp } from "../../../hooks/useFormattedTimestamp";
import { useDataFormatter } from "../../../hooks/useDataFormatter";
import { getChatThreadRoute } from "../../../routers/routeUtils";

const ChatItem = ({ chat, isLast, userId }: { chat: Chat; isLast: boolean; userId: number | null }) => {
  const [sendToUser, setSendToUser] = useState<UserInfo>();

  const { lastMessage, participants, id, lastSeen } = chat;
  const sendToUserID = participants?.find((id) => id !== userId);
  const IS_SEND_TO_USER_ONLINE: boolean = sendToUserID ? isUserOnline(lastSeen[sendToUserID]) : false;
  const IS_SEEN: boolean = lastMessage.senderId === userId || lastSeen[userId!] != 0;

  const { fullName, firstTwoChr, media, role } = useDataFormatter(sendToUser);
  useEffect(() => {
    if (!sendToUserID) return;

    const getSendToUserData = async () => {
      try {
        const userData = await getEntityDetails(`${sendToUserID}`);
        setSendToUser(userData);
      } catch (error) {
        // console.error("Failed to fetch user data in ChatItem:", error);
      }
    };

    getSendToUserData();
  }, [sendToUserID]);

  const CHAT_LINK = getChatThreadRoute(id, sendToUserID);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: "5px",
        paddingBottom: isLast ? 0 : "20px",
        borderBottom: isLast ? "none" : "1px solid #EAECF0",
        marginBottom: isLast ? 0 : "14px",
      }}
    >
      <Link to={CHAT_LINK} style={{ textDecoration: "none" }}>
        <Box sx={{ display: "flex", columnGap: "10px", marginBottom: "" }}>
          <Avatar
            sx={{
              width: "36px",
              height: "36px",
              fontSize: "18px",
              position: "relative",
              overflow: "visible",
            }}
            src={media}
          >
            {firstTwoChr ? firstTwoChr : ""}
            {IS_SEND_TO_USER_ONLINE ? (
              <ActiveIcon
                fill="none"
                sx={{
                  width: "12px",
                  height: "12px",
                  position: "absolute",
                  bottom: "-4px",
                  right: "-4px",
                  border: "4px solid #FFF",
                  borderRadius: "50%",
                }}
              />
            ) : (
              ""
            )}
          </Avatar>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "calc(100% - 46px)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body14SemiBold"
                color={colors.Green100}
                sx={{
                  maxWidth: "50%",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={fullName}
              >
                {fullName}
              </Typography>
              <Typography variant="body12Regular" color={colors.gray400}>
                {useFormattedTimestamp(lastMessage?.createdAt as number)}
              </Typography>
            </Box>
            <Typography variant="body12Regular" color={colors.dark600} sx={{ marginBottom: "12px" }}>
              {role}
            </Typography>
            <Typography
              variant={IS_SEEN ? "body12Regular" : "body12Bold"}
              color={colors.black100}
              sx={{
                display: "block",
                maxWidth: "90%",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              {lastMessage?.data.text}
            </Typography>
          </Box>
        </Box>
      </Link>
    </Box>
  );
};

export default ChatItem;

export const ChatLoadingItem = ({ isLast }: { isLast: boolean }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      rowGap: "5px",
      paddingBottom: isLast ? 0 : "20px",
      borderBottom: isLast ? "none" : "1px solid #EAECF0",
      marginBottom: isLast ? 0 : "14px",
    }}
  >
    <Box
      sx={{
        display: "flex",
        columnGap: "10px",
        alignItems: "center",
      }}
    >
      {/* Avatar Skeleton */}
      <Skeleton variant="circular" width={48} height={48} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "calc(100% - 53px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
          }}
        >
          {/* Name and Timestamp Skeleton */}
          <Skeleton variant="text" width="40%" height={20} />
          <Skeleton variant="text" width="20%" height={16} />
        </Box>

        {/* Role Skeleton */}
        <Skeleton variant="text" width="30%" height={16} sx={{ marginBottom: "12px" }} />

        {/* Message Preview Skeleton */}
        <Skeleton
          variant="text"
          width="90%"
          height={16}
          sx={{
            maxWidth: "90%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        />
      </Box>
    </Box>
  </Box>
);
